<script setup lang="ts">
import { computed, ref } from 'vue';
import {useI18n} from 'vue-i18n';
const {t} = useI18n();
import {
        useDashboardStore,
        useSessionStore
} from 'src/store';
import {
  TaskType,
} from 'src/constants/enums';
import { filterAndSortTasks} from '@/utils/arrayUtil.ts';

import TheTodaysTasksCategory from 'src/pages/DashboardHome/components/TheTodaysTasksCategory.vue';
import Box from 'src/components/BoxContainer.vue';
import { useEventBus } from '@/utils/eventBus';
const dashboardStore = useDashboardStore();
const logoutPopup = ref<boolean>(false);
const eventBus = useEventBus();
const sessionStore = useSessionStore();

const isLoading = computed(() => dashboardStore.isLoading);
const isLoadingAppDownloadInfo = computed(() => !sessionStore.appDownloadInfo);
const taskType = computed(() => TaskType);
const todaysDate = computed(() => dashboardStore.todaysDate);
const isPIQandDisabled = computed(() => dashboardStore.patientConfig?.isDischarged);
const displaySurveys = computed(() => dashboardStore?.patientConfig?.surveys?.isAdherenceEnabled ?? false);
const userOS = computed(() => dashboardStore.patient?.userOS);

const surveyTasks = computed(() => {
  const tasks = dashboardStore.tasks;
  return filterAndSortTasks(TaskType.survey, tasks);
});

async function onClickLogout() {
  logoutPopup.value = false;
  eventBus.emit('user-log-out', 'Welcome');
}
</script>

<template>

    <!-- Log Out Modal -->
  <zbm-dialog v-model="logoutPopup">

    <!-- Header -->
    <div class="row q-dialog__header">
      <div class="col-auto">

        <!-- Exclamation Circle Icon -->
        <q-icon color="dark-teal q-pb-sm" name="fa-solid fa-circle-exclamation" size="42px"/>

        <!-- Title -->
        <h5>{{ t('settings_log_out_title_case') }}</h5>
      </div>
    </div>

    <!-- Body -->
    <div class="row q-dialog__body">
      <div class="col-auto">
        <p>{{ t('settings_log_out_confirm') }}</p>
      </div>
    </div>

    <!-- Footer (CTA button(s)) -->
    <div class="row q-dialog__footer">

      <!-- No -->
      <div class="col-auto">
        <zbm-btn v-close-popup :color="$zb.enums.ButtonStdColorEnum.accentText" :height="$zb.enums.ButtonHeightEnum.md"
                 :label="t('gen_no')"/>
      </div>

      <!-- Yes -->
      <div class="col-auto">
        <zbm-btn @click="onClickLogout()" :color="$zb.enums.ButtonStdColorEnum.accent"
                 :height="$zb.enums.ButtonHeightEnum.md" :label="t('gen_yes')"/>
      </div>
    </div>
  </zbm-dialog>

  <!-- If patient is NOT PIQ & discharged. -->
  <div v-if="!isPIQandDisabled">

    <!-- Heading -->
    <div class="row items-center dash-home__heading">

      <!-- Title -->
      <div class="col">
        <h1>
          <zbm-skeleton-loader height="34px"
                               :loading="isLoading"
                               width="100%">
            {{ t('home_tasks') }}
          </zbm-skeleton-loader>
        </h1>
      </div>
      <div class="flex-br" />

      <!-- Date -->
      <div class="col-auto">
        <zbm-skeleton-loader height="24px"
                             :loading="isLoading"
                             width="100px">
          {{ todaysDate }}
        </zbm-skeleton-loader>
      </div>
    </div>

    <div class="row dash-home__tasks">
      <div class="col">
        <!-- Surveys Category -->
        <zbm-skeleton-loader class="mb-18"
                             height="70px"
                             :loading="isLoading"
                             width="100%"
                             v-if="displaySurveys">
          <TheTodaysTasksCategory :label="t('gen_surveys')"
                                  :tasks="surveyTasks"
                                  :taskType="taskType.survey"
                                  :isOpen="true"
                                  data-testid="surveyTasks">
            <template #icon>
              <q-icon name="fa-solid fa-clipboard-list"
                      size="29px" />
            </template>
          </TheTodaysTasksCategory>
        </zbm-skeleton-loader>
      </div>
    </div>

        <!-- My Recovery Progress -->
    <div class="row dash-home__careteam-support" data-testid="careTeamSupport">
      <div class="col">

        <!-- Illustration -->
         <zbm-skeleton-loader class="mb-20" height="50px" width="50%" :loading="isLoading">
          <div class="row justify-center">
            <img src="/img/medical-researcher.svg" />
          </div>
        </zbm-skeleton-loader>

        <zbm-skeleton-loader class="mb-20" height="100px" width="100%" :loading="isLoading">
          <div class="row justify-center">
            <p class="font-size-18">{{ t('your_careteam_is_here') }}</p>
          </div>
        </zbm-skeleton-loader>

        <!-- Get iOS App Button -->
        <div v-if="sessionStore.appDownloadInfo?.preambleResult.appStoreLinks?.iosurl || isLoadingAppDownloadInfo" class="row justify-center get-ios">
          <zbm-skeleton-loader class="q-btn--276 mb-20" height="50px" width="70%" :loading="isLoadingAppDownloadInfo">
            <zbm-btn class="q-btn--276" :height="$zb.enums.ButtonHeightEnum.lg" :color="$zb.enums.ButtonStdColorEnum.accentText"
              :href="sessionStore.appDownloadInfo?.preambleResult.appStoreLinks?.iosurl" icon="fa-brands fa-apple"
              :label="$t('pwe_get_ios_app')" target="_blank" data-testid="getAppleApp" />
          </zbm-skeleton-loader>
        </div>

        <!-- Get Android App Button -->
        <div v-if="sessionStore.appDownloadInfo?.preambleResult.appStoreLinks?.androidURL || isLoadingAppDownloadInfo" class="row justify-center get-android">
          <zbm-skeleton-loader class="q-btn--276 mb-20" height="50px" width="90%" :loading="isLoadingAppDownloadInfo">
            <zbm-btn class="q-btn--276" :height="$zb.enums.ButtonHeightEnum.lg" :color="$zb.enums.ButtonStdColorEnum.accentText"
              :href="sessionStore.appDownloadInfo?.preambleResult.appStoreLinks?.androidURL" icon="fa-brands fa-android"
              :label="$t('pwe_get_android_app')" target="_blank" data-testid="getAndroidApp"/>
          </zbm-skeleton-loader>
        </div>

        <div class="row justify-center">
          <zbm-skeleton-loader height="50px" width="40%" :loading="isLoading">
            <zbm-btn-link @click="logoutPopup = true" icon="fa-solid fa-right-from-bracket" 
              :label="t('settings_log_out_title_case')" :color="$zb.enums.ButtonStdColorEnum.accentText"
              :height="$zb.enums.ButtonHeightEnum.lg" />
          </zbm-skeleton-loader>
        </div>
      </div>
    </div>
  </div>

  <!-- If patient IS PIQ & discharged -->
  <div v-else
       class="row dash-home__defeatured">
    <div class="col">
      <Box :box-bkgd-color="$zb.enums.BoxBackgroundColorEnum.grey"
           :box-padding="$zb.enums.BoxPaddingEnum.p303055"
           class="text-center">
        <img src="/img/defeatured.svg" alt="" />
        <h2 class="text-secondary">{{ t('patient_discharge_stats') }}</h2>
        <p>{{ t('patient_discharge_stats_desc') }}</p>
      </Box>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';

.dash-home {
  $dash-home-border: 1px solid $border-clr;

  // Heading
  &__heading { // .row
    margin-block-end: 21px;
  }

  // Today's Tasks
  &__tasks { // .row
    margin-block-end: 20px;
  }

  // Today's Tasks Priority
  &__priority { // .row
    padding-block-end: 20px;
    border-block-start: $dash-home-border;
    margin-block-end: 25px;
  }
  
  @media (max-width: 359px) {

    // My Progress
    &__progress { // .row

      // Metrics
      &-metrics > [class*="col"] {
        flex: 1000 1 0;
      }

      // About
      &-about { // .row
        display: none;
      }
    }
  }

  @media (min-width: 400px) {

    // Heading
    &__heading { // .row

      > [class*="col"] {
        padding-inline: 20px;

        &:first-child {
          padding-inline-start: 0;
        }

        &:last-child {
          padding-inline-end: 0;
        }
      }

      .flex-br {
        display: none;
      }
    }
  }
}
</style>
